import { Outlet, useNavigate, useRouterState } from "@tanstack/react-router";
import { Sidebar } from "@shared/components/Sidebar/Sidebar";
import { useAuthStore } from "@shared/store/auth";
import { abortActiveRequests } from "@shared/api/client";
import { useEffect, useRef, useState } from "react";

export function AppLayout() {
  const logout = useAuthStore((s) => s.logout);
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const outletScrollRef = useRef<HTMLDivElement | null>(null);
  const [logoutPending, setLogoutPending] = useState(false);

  const onLogout = () => {
    if (logoutPending) return;

    setLogoutPending(true);
    abortActiveRequests("logout-navigation");
    void logout();

    void navigate({ to: "/login", replace: true });
  };

  useEffect(() => {
    const scrollContainer = outletScrollRef.current;

    if (!scrollContainer) return;

    scrollContainer.scrollTo({
      top: 0,
      left: 0,
      behavior: "auto",
    });
  }, [pathname]);

  if (logoutPending) {
    return (
      <div className="h-screen overflow-hidden bg-[#dbe9e2] p-3">
        <div className="h-full rounded-[36px] border border-white/80 bg-white/78 shadow-[0_28px_90px_rgba(15,23,42,.13)] backdrop-blur-xl" />
      </div>
    );
  }

  return (
    <div className="h-screen overflow-hidden bg-[#dbe9e2] p-3">
      <div className="flex h-full overflow-hidden rounded-[36px] border border-white/80 bg-white/78 shadow-[0_28px_90px_rgba(15,23,42,.13)] backdrop-blur-xl">
        <Sidebar onLogout={onLogout} logoutPending={logoutPending} />

        <main className="relative min-w-0 flex-1 overflow-hidden bg-[radial-gradient(circle_at_8%_0%,rgba(11,74,47,.10),transparent_28%),radial-gradient(circle_at_92%_8%,rgba(0,208,156,.07),transparent_24%),linear-gradient(180deg,#f8fbf9_0%,#edf5f1_100%)] overscroll-x-none">
          <div className="pointer-events-none absolute inset-x-0 top-0 h-44 bg-gradient-to-b from-white/90 to-transparent" />
          <div className="pointer-events-none absolute right-[-120px] top-[-120px] h-[340px] w-[340px] rounded-full bg-[#00d09c]/12 blur-3xl" />
          <div className="pointer-events-none absolute bottom-[-180px] left-[20%] h-[390px] w-[390px] rounded-full bg-[#0b4a2f]/10 blur-3xl" />
          <div className="pointer-events-none absolute inset-0 opacity-[0.18]">
            <div className="h-full w-full bg-[radial-gradient(rgba(15,23,42,.13)_0.6px,transparent_0.6px)] bg-[size:18px_18px]" />
          </div>

          <div
            ref={outletScrollRef}
            className="relative z-10 h-full min-h-0 min-w-0 max-w-full overflow-x-hidden overflow-y-auto overscroll-contain"
          >
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
